import { XCircle } from 'lucide-react';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import {
  checkPasswordRequirements,
  getPasswordStatusText,
  getPasswordStrength,
  getStrengthColor,
  isPasswordValid,
  passwordRequirements,
} from '@/features/auth/validation/password-validation';
import { cn } from '@/shared/utils/cn';

interface PasswordStrengthIndicatorProps {
  password: string;
  onRequirementsMet?: (met: boolean) => void;
}

export const PasswordStrengthIndicator = ({
  password,
  onRequirementsMet,
}: PasswordStrengthIndicatorProps) => {
  const { t } = useTranslation('common', {
    keyPrefix: 'form_field.password.requirements',
  });
  const requirements = checkPasswordRequirements(password, t);
  const strength = getPasswordStrength(password);
  const minReqsMet = isPasswordValid(password);

  useEffect(() => {
    onRequirementsMet?.(minReqsMet);
  }, [minReqsMet, onRequirementsMet]);

  const strengthColor = getStrengthColor(strength);

  return (
    <div className="space-y-3 pb-4 ps-2 sm:space-y-4 sm:ps-3 sm:pt-[10px]">
      <div className="flex gap-1 sm:gap-1.5">
        {Array.from({ length: passwordRequirements().length }).map(
          (_, index) => (
            <div
              key={index}
              className={cn(
                'h-[2px] w-12 rounded-full sm:h-[3px] sm:w-16 md:w-[70px]',
                index < strength
                  ? {
                      'bg-error-red': strengthColor === 'error-red',
                      'bg-success-green': strengthColor === 'success-green',
                    }
                  : 'bg-custom-light-grey',
              )}
            />
          ),
        )}
      </div>
      <div
        className={cn('text-xs font-medium sm:text-sm md:text-xs', {
          'text-error-red': strengthColor === 'error-red',
          'text-success-green': strengthColor === 'success-green',
        })}
      >
        {getPasswordStatusText(strength, t)}
      </div>
      {!minReqsMet && (
        <div className="grid grid-cols-1 gap-1 pb-4 sm:gap-1.5 lg:grid-flow-col lg:grid-cols-2 lg:grid-rows-3">
          {requirements
            .filter((req) => !req.met)
            .map((req, index) => (
              <div
                key={`${req.message}-${index}`}
                className="flex items-center gap-1 text-xs font-medium text-error-red sm:gap-1.5 sm:text-sm md:text-xs"
              >
                <XCircle className="h-3 w-3 flex-shrink-0 sm:h-4 sm:w-4" />
                <span className="leading-tight">{req.message}</span>
              </div>
            ))}
        </div>
      )}
    </div>
  );
};
